import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {useHistory, useParams} from 'react-router-dom';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {SelectItem} from '../../../../components/Select';
import {
    MenuContent,
    MenuContentType,
    MenuEntity,
} from '../../../../store/control/state';
import {useSelector} from 'react-redux';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {ControlTypeModel} from '../../../../types/control';
import {useQuestion} from '../../../../hooks/question';

export const TYPE_NAME_MAP: Record<MenuContentType, string> = {
    control: 'Kontrol',
    media: 'Medya',
};

const TYPE_OPTION_LIST: SelectItem<MenuContentType>[] =
    (Object.entries(TYPE_NAME_MAP) as [MenuContentType, string][])
        .map(([type, name]) => ({label: name, value: type}));

export const ContentEditScreen = memo(function ContentEditScreen() {
    const params = useParams<{ type: string; rel_id: string; menu_id: string; }>();
    const history = useHistory();
    const {success, failure} = useMessage();
    const question = useQuestion();
    const [rpc] = useRPC();

    const backUrl = useMemo(() => `/settings/left-menu/${params.menu_id}/contents/`, [params.menu_id]);

    const [selectedContent, setSelectedContent] = useState<MenuContent>(null);
    const menuList = useSelector(state => state.control.menuList);
    const selectedMenu = useMemo(() => menuList.find((m) => m.id === params.menu_id), [menuList, params.menu_id]);
    const sortedContentList = useMemo(() => selectedMenu.content_list ?
        selectedMenu.content_list.sort((a, b) => a.order - b.order) : [], [selectedMenu.content_list]);

    const controlTypes = useSelector(state => state.control.controlTypeList);
    const controlTypeOptionList: SelectItem<Omit<ControlTypeModel, 'image_set'>>[] =
        useMemo(() => controlTypes
            .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
            .map(controlType => ({
                value: controlType,
                label: controlType.name,
            })), [controlTypes]);

    const mediaTypeOptionsList = useMemo(() => [{label: 'Klima', value: 'air'}, {label: 'Diğer', value: 'other'}], []);

    const [selectedType, setSelectedType] = useState<SelectItem<MenuContentType>>(null);
    const [selectedControlOption, setSelectedControlOption] = useState<SelectItem<Omit<ControlTypeModel, 'image_set'>>>(null);
    const [selectedMediaOption, setSelectedMediaOption] = useState<SelectItem<string>>(null);

    const onBackPressed = useCallback(() => {
        history.push(backUrl);
    }, [history, backUrl]);


    const isFieldsValid = useCallback((): boolean => {
        if (selectedType == null) {
            failure('Lütfen geçerli bir tip seçiniz.');

            return false;
        }

        if (selectedType.value === 'control') {
            if (selectedControlOption == null) {
                failure('Lütfen geçerli bir kontrol tipi seçiniz.');

                return false;
            }
        }

        if (selectedType.value === 'media') {
            if (selectedMediaOption == null) {
                failure('Lütfen geçerli bir medya tipi seçiniz.');

                return false;
            }
        }

        const relationalIdExists = selectedType.value === 'control' ? selectedControlOption.value.id :
            selectedType.value === 'media' ? selectedMediaOption.value : null;
        const contentExists = sortedContentList.find((c) => (c.type === selectedType.value && (relationalIdExists ? c?.relational_id === relationalIdExists : true)));

        if ((selectedContent !== contentExists) && contentExists) {
            failure('Bu menü içeriği önceden eklenmiş!');

            return false;
        }

        return true;
    }, [selectedType, selectedControlOption, selectedMediaOption, sortedContentList, selectedContent, failure]);

    const returnPushedArray = useCallback((array, item) => {
        const tempArray = array;
        array.push(item);
        return tempArray;
    }, []);

    const onDeletePressed = useCallback(() => {
        question('İçeriği silmek istediğinizden emin misiniz?', () => {
            const newContentList = sortedContentList.filter(c => !(c.type === params.type && (params.rel_id ? c.relational_id === params.rel_id : true)));

            const menuEntity: MenuEntity = {
                ...selectedMenu,
                content_list: newContentList,
            };

            return rpc.updateMenu(menuEntity)
                .then(() => {
                    success(`İçerik başarılı bir şekilde silindi.`);
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [failure, onBackPressed, params.rel_id, params.type, question, rpc, selectedMenu, sortedContentList, success]);

    const onCreatePressed = useCallback(() => {
            if (!isFieldsValid()) {
                return;
            }

            const order = sortedContentList.length === 0 ? 0 :
                sortedContentList.sort((a, b) => b.order - a.order)[0].order + 1;

            const menuContent: MenuContent = {
                type: selectedType.value,
                relational_id: selectedType.value === 'control' ? selectedControlOption.value.id :
                    selectedType.value === 'media' ? selectedMediaOption.value : null,
                order: selectedContent ? selectedContent.order : order,
            };

            const newContentList = params.type ? sortedContentList.map(c => {
                if (c.type === params.type && (params.rel_id ? c.relational_id === params.rel_id : true)) {
                    c = menuContent;
                }
                return c;
            }) : returnPushedArray(sortedContentList, menuContent);

            const menuEntity: MenuEntity = {
                ...selectedMenu,
                content_list: newContentList,
            };

            rpc.updateMenu(menuEntity).then(() => {
                success('Başarılı bir şekilde ' + (params?.type ? 'düzenlendi.' : 'oluşturuldu.'));
                onBackPressed();
            }).catch(failure);
        },
        [
            isFieldsValid,
            sortedContentList,
            selectedType?.value,
            selectedControlOption?.value,
            selectedMediaOption?.value,
            selectedMenu,
            rpc,
            failure,
            success,
            onBackPressed,
            params.rel_id,
            params.type,
            returnPushedArray,
            selectedContent,
        ]);


    const buttons = useMemo(() =>
        [
            {
                text: 'SİL',
                className: 'mulberry-wood',
                onClick: onDeletePressed,
                hidden: !(params.type),
            },
            {
                text: 'KAYDET',
                className: 'green',
                onClick: onCreatePressed,
            },
            {
                text: 'İPTAL',
                className: 'red',
                url: backUrl,
            },
        ], [onDeletePressed, params.type, onCreatePressed, backUrl],
    );

    useEffect(() => {
        if (params.type == null) {
            return;
        }

        const content = sortedContentList.find((c) => (c.type === params.type && (params.rel_id ? c?.relational_id === params?.rel_id : true)));

        if (content == null) {
            return;
        }

        setSelectedContent(content);
        setSelectedType(TYPE_OPTION_LIST.find(typeOption => typeOption.value === content.type));
        const selectedControl = content.type === 'control' &&
            controlTypeOptionList.find(controlOption => controlOption.value.id === content.relational_id);
        setSelectedControlOption(selectedControl || controlTypeOptionList[0] || null);
        const selectedMedia = content.type === 'media' &&
            mediaTypeOptionsList.find(mediaOption => mediaOption.value === content.relational_id);
        setSelectedMediaOption(selectedMedia || mediaTypeOptionsList[0] || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (params.type != null) {
            return;
        }

        setSelectedType(TYPE_OPTION_LIST[0]);
        setSelectedControlOption(controlTypeOptionList[0] || null);
        setSelectedMediaOption(mediaTypeOptionsList[0] || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className='service-edit'
                backUrl={backUrl}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingSelectComponent
                            label='Tip'
                            options={TYPE_OPTION_LIST}
                            onChange={setSelectedType}
                            value={selectedType}/>
                        {
                            renderIf(selectedType?.value === 'control')(() =>
                                <SettingSelectComponent
                                    label={selectedType?.label}
                                    options={controlTypeOptionList}
                                    onChange={setSelectedControlOption}
                                    value={selectedControlOption}/>,
                            )
                        }
                        {
                            renderIf(selectedType?.value === 'media')(() =>
                                <SettingSelectComponent
                                    label={selectedType?.label}
                                    options={mediaTypeOptionsList}
                                    onChange={setSelectedMediaOption}
                                    value={selectedMediaOption}/>,
                            )
                        }
                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
